import {
  MentionsTimelineResponseSchema as ContractMentionsTimelineResponseSchema,
  TimelinePageResponseSchema as ContractTimelinePageResponseSchema,
} from '@sweetistics/contracts';
import { CanonicalTweetSchema } from '@sweetistics/contracts/tweets-canonical';
import { type ZodType, z } from 'zod';
import type { Tweet } from '../../../../types/tweet';
import type { LinkMetadata } from '../../../fetch-metadata';
import { normalizeLinkMetadataRecord } from '../../../url-metadata/normalize';

type ContractTimelinePageResponse = z.infer<typeof ContractTimelinePageResponseSchema>;
type ContractMentionsTimelineResponse = z.infer<typeof ContractMentionsTimelineResponseSchema>;

const timelineProfileSchema = z
  .object({
    id: z.string().optional().nullable(),
    username: z.string().optional().nullable(),
    name: z.string().optional().nullable(),
    profileImageUrl: z.string().optional().nullable(),
    verifiedType: z.union([z.string(), z.number(), z.boolean()]).optional().nullable(),
    createdAt: z.union([z.string(), z.number(), z.date()]).optional().nullable(),
    description: z.string().optional().nullable(),
    affiliations: z.array(z.unknown()).optional().nullable(),
    primaryAffiliation: z
      .object({
        organizationProfileId: z.string().optional().nullable(),
        organizationName: z.string().optional().nullable(),
        organizationUsername: z.string().optional().nullable(),
        badgeUrl: z.string().optional().nullable(),
        url: z.string().optional().nullable(),
      })
      .partial()
      .optional()
      .nullable(),
    publicMetrics: z.record(z.string(), z.unknown()).optional().nullable(),
  })
  .passthrough();

const timelineProfileRecordSchema = z.record(z.string(), timelineProfileSchema);

export type TimelinePageResponse = Omit<ContractTimelinePageResponse, 'tweets' | 'inlineTweets'> & {
  tweets: Record<string, Tweet>;
  inlineTweets?: Record<string, Tweet>;
  profiles: z.infer<typeof timelineProfileRecordSchema>;
  urlMetadata: Record<string, LinkMetadata>;
};

export type MentionsTimelineResponse = Omit<ContractMentionsTimelineResponse, 'tweets'> & {
  tweets: Tweet[];
};

const tweetSchema: ZodType<Tweet> = CanonicalTweetSchema;

const parseCanonicalTweet = (input: unknown): Tweet => tweetSchema.parse(input);

export const TimelinePageResponseSchema = ContractTimelinePageResponseSchema.transform<TimelinePageResponse>(
  (value: ContractTimelinePageResponse): TimelinePageResponse => {
    const rawTweets = value.tweets ?? {};
    const parsedTweets: Record<string, Tweet> = {};
    for (const [id, tweet] of Object.entries(rawTweets)) {
      parsedTweets[id] = parseCanonicalTweet(tweet);
    }

    const { inlineTweets: rawInlineTweets = {}, ...restValue } = value;
    const parsedInlineTweetsEntries: [string, Tweet][] = [];
    for (const [id, tweet] of Object.entries(rawInlineTweets)) {
      parsedInlineTweetsEntries.push([id, parseCanonicalTweet(tweet)]);
    }
    const parsedInlineTweets =
      parsedInlineTweetsEntries.length > 0 ? Object.fromEntries(parsedInlineTweetsEntries) : undefined;

    const parsedProfiles = timelineProfileRecordSchema.parse(value.profiles ?? {});
    const parsedUrlMetadata = normalizeLinkMetadataRecord(value.urlMetadata) ?? {};

    const response: TimelinePageResponse = {
      ...restValue,
      tweets: parsedTweets,
      profiles: parsedProfiles,
      urlMetadata: parsedUrlMetadata,
    };
    if (parsedInlineTweets) {
      response.inlineTweets = parsedInlineTweets;
    }
    return response;
  }
);

export const MentionsTimelineResponseSchema =
  ContractMentionsTimelineResponseSchema.transform<MentionsTimelineResponse>(
    (value: ContractMentionsTimelineResponse): MentionsTimelineResponse => ({
      ...value,
      tweets: value.tweets.map((tweet) => parseCanonicalTweet(tweet)),
    })
  );

export {
  ProfileStatusResponseSchema,
  TimelineSnapshotResponseSchema,
  UserAnalysisCacheResponseSchema,
  UserStatusResponseSchema,
} from '@sweetistics/contracts';
export { TimelineAnalysisMetadataSchema } from '../../../../types/timeline-analysis';
